import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export type GameChoice = { id: string; label: string };
export type FamilyGameRound = {
  roundId: string; playDate: string; type: "family-vote" | "true-false" | "multiple-choice" | "know-family";
  eyebrow: string; question: string; choices: GameChoice[]; allowDuplicates: boolean;
  status: "open" | "scoring" | "revealed" | "skipped"; spotlightUid?: string | null;
  winningChoices?: string[]; correctChoice?: string | null; noConsensus?: boolean; resultLabel?: string;
  correctGuessCount?: number; requiredCorrectGuesses?: number; spotlightPoints?: number;
};
export type FamilyGameScore = { uid: string; name: string; faceoff: number; trivia: number; streak: number; hunt: number; famBook: number; total: number };
export type FamilyGameHistoryVote = {
  uid: string; memberName: string; choice: string | null; choiceLabel: string; won: boolean; missed?: boolean; points: number;
};
export type FamilyGameHistoryRound = {
  roundId: string; playDate: string; type: FamilyGameRound["type"]; eyebrow: string; question: string;
  choices: GameChoice[]; winningChoices: string[]; correctChoice?: string | null; noConsensus?: boolean;
  resultLabel: string; votes: FamilyGameHistoryVote[]; spotlightUid?: string | null;
  correctGuessCount?: number; requiredCorrectGuesses?: number; spotlightPoints?: number;
};
export type FamilyGameWeeklyRecapRow = {
  uid: string; name: string; rank: number; faceoffPoints: number; streakPoints: number;
  homeHuntPoints: number; famBookPoints: number; famBookContributions: number; totalPoints: number; famBucksAwarded: number;
};
export type FamilyGameWeeklyRecap = {
  weekStart: string; weekEnd: string; rows: FamilyGameWeeklyRecapRow[]; generatedAt?: unknown;
};
export type FamilyGameState = {
  round: FamilyGameRound; ownVote: string | null; votedCount: number; memberCount: number;
  votedUids: string[]; votes: Array<{ uid: string; choice: string }>; scores?: FamilyGameScore[];
  scoresByPeriod?: Record<string, FamilyGameScore[]>; history?: FamilyGameHistoryRound[];
  yesterdayResult?: { playDate: string; answer: string; correctCount: number; guessCount: number } | null;
  weeklyRecap?: FamilyGameWeeklyRecap | null;
};

const recordVisit = httpsCallable<{ householdId: string; availableHuntSections?: string[] }, { playDate: string }>(functions, "recordFamilyVisit");
const getGame = httpsCallable<{ householdId: string }, FamilyGameState>(functions, "getFamilyGame");
const getSummary = httpsCallable<{ householdId: string }, Pick<FamilyGameState, "scores" | "scoresByPeriod" | "yesterdayResult" | "weeklyRecap">>(functions, "getFamilyGameSummary");
const getHistory = httpsCallable<{ householdId: string }, { history: FamilyGameHistoryRound[] }>(functions, "getFamilyGameHistory");
const submitVote = httpsCallable<{ householdId: string; choice: string }, FamilyGameState>(functions, "submitFamilyGameVote");
const adminGame = httpsCallable<Record<string, unknown>, FamilyGameState>(functions, "adminFamilyGame");

export async function recordFamilyVisit(householdId: string, availableHuntSections?: string[]) { return (await recordVisit({ householdId, availableHuntSections })).data; }
export async function getFamilyGame(householdId: string) { return (await getGame({ householdId })).data; }
export async function getFamilyGameSummary(householdId: string) { return (await getSummary({ householdId })).data; }
export async function getFamilyGameHistory(householdId: string) { return (await getHistory({ householdId })).data.history; }
export async function submitFamilyGameVote(householdId: string, choice: string) { return (await submitVote({ householdId, choice })).data; }
export async function runFamilyGameAdminAction(householdId: string, action: string, details: Record<string, unknown> = {}) {
  return (await adminGame({ householdId, action, ...details })).data;
}
